/**
 * goyCard.tsx
 * 
 * @copyright jnakaso.com
 * 
 *  Golfer Of The Year
 * 
 */
import { ActionIcon, Card, Center, Flex, Popover, ScrollArea, Table, Text } from "@mantine/core";
import { IconInfoSmall } from '@tabler/icons-react';
import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { Link } from "react-router-dom";
import { seasonService } from "src/services/seasonService";
import { storageService } from "src/services/storageService";
import { tournamentService } from "src/services/tournamentService";

export function StandingsTable(props: { season: number, standings: any[] }) {
    const [playerRows, setPlayerRows] = useState<any[]>([]);
    const [tournaments, setTournaments] = useState<any[]>([]);
    const [tournamentColumns, setTournamentColumns] = useState<any[]>([]);

    useEffect(() => {
        if (props.season) {
            seasonService.findByYear(props.season)
                .then(ss => {
                    tournamentService.findBySeason(ss!)
                        .then(tt => setTournaments(tt))
                        .catch(err => console.error(err));
                })
                .catch(err => console.error(err));
        }
        if (props.standings) {
            setPlayerRows(props.standings.map((q: any) => {
                return (
                    <Table.Tr key={`${q.playerId}`}>
                        <Table.Td><Text fw={600} >{q.player}</Text></Table.Td>
                        <Table.Td>{q.total.toFixed(2)}</Table.Td>
                        {Object.values(q.scores).map((v: any, index) =>
                            <Table.Td key={index}>{v === 0 ? "-" : v.toFixed(2)}</Table.Td>)
                        }
                    </Table.Tr>
                )
            }))
        }
    }, [props]);

    useEffect(() => {
        if (tournaments) {
            setTournamentColumns(tournaments.map((t: any, index) =>
                <Table.Th key={index}>{t.date.slice(5)}</Table.Th>))
        }
    }, [tournaments]);
    
    return <Table>
        <Table.Thead>
            <Table.Tr>
                <Table.Th w={200}>Player</Table.Th>
                <Table.Th w={150}>Points</Table.Th>
                {tournamentColumns}
            </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
            {playerRows}
        </Table.Tbody>
    </Table>
}

export function GoyCard(props: { season: number, standings: any[] }) {
    const [rules, setRules] = useState('No content found');

    useEffect(() => {
        storageService.getInfoFile('goy-rules.md')
            .then((text) => setRules(text))
            .catch((e) => console.error(e));
    }, [props])


    return <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Card.Section>

            <Center h={80} bg="var(--mantine-color-gray-light)">
                <Text fw={800} >{props.season} G.O.Y.
                    <Popover width={400} position="bottom" withArrow shadow="md">
                        <Popover.Target>
                            <ActionIcon
                                ml="0.5rem"
                                variant="gradient"
                                size="xs"
                                aria-label="GOY rules"
                                gradient={{ from: 'blue', to: 'cyan', deg: 90 }}>
                                <IconInfoSmall />
                            </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Markdown>{rules}</Markdown>
                        </Popover.Dropdown>
                    </Popover>
                </Text>
            </Center>
        </Card.Section>
        <ScrollArea style={{ overflowY: "auto" }}>
            <StandingsTable season={props.season} standings={props.standings} />
        </ScrollArea>
    </Card>
}

function ExpandLink(props: { href: string }) {
    return <Flex justify="flex-end">
        <Link to={props.href}>Expand</Link>
    </Flex>
}

export function GoyLeaderboardCard(props: { season: number, standings: any[], maxRows?: number }) {
    const [players, setPlayers] = useState<any[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    useEffect(() => {
        if (props.standings) {
            if (props.maxRows) {
                // assume standings is sorted
                const lowest = props.standings[Math.min(props.maxRows - 1, props.standings.length)];
                const lowTotal = lowest ? lowest.total : 0;
                const trimmed = props.standings.filter(s => s.total >= lowTotal)
                setPlayers(trimmed);
            } else {
                setPlayers(props.standings);
            }
        }
    }, [props])

    useEffect(() => {
        if (players) {
            setRows(players.map((q: any) =>
                <Table.Tr key={`${q.player}`}>
                    <Table.Td> <Text fw={600} >{q.player}</Text></Table.Td>
                    <Table.Td>{q.total.toFixed(2)}</Table.Td>
                </Table.Tr>
            ))
        }
    }, [players])

    return <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Card.Section>
            <Center h={80} bg="var(--mantine-color-gray-light)">
                <Text fw={800} >{props.season} G.O.Y. Leaderboard</Text>
            </Center>
        </Card.Section>
        <Table>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th w={200}>Player</Table.Th>
                    <Table.Th w={150}>Points</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows}
            </Table.Tbody>
        </Table>

        <ExpandLink href={"/goy"} />
    </Card>
}
